import { t } from 'i18next';
import { Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { PointHeightInterpolationMode } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/features/interpolate-point-heights';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { slopePercentToHeightDiff } from '../../../../../../../utils/math/slope';
import {
  DesignNode,
  RevertChange,
} from '../../../../../../elements/design/design-node/design-node';
import { PointId } from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { flatDistance, interpolate } from '../../../../../../utilities/math-helpers';
import { ActionName, DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { getPointIdArray } from '../../utils/select';
import { PolylinePath, selectPolylineUtils } from './select-polyline-utils';

const activateInterpolatePointHeightsFeature = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.interpolatePointHeights', { ns: 'skyviewDesign' }),
    action: () => {
      const alreadyActive =
        tool.actionInProgress?.actionName === ActionName.InterpolatePointHeights;

      tool.activeFeature = DesignFeature.InterpolatePointHeights;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });
      tool.contextMenuCallback?.();

      if (!alreadyActive) {
        interpolatePointHeightsUtils.initInterpolatePointHeightsAction(tool);
      }
    },
    hotkey: 'I',
  };
};

const initInterpolatePointHeightsAction = (tool: DesignTool): void => {
  return selectPolylineUtils.initAction(
    tool,
    ActionName.InterpolatePointHeights,
    (paths) => {
      let changes: RevertChange[] = [];
      const interpolatedPoints = getInterpolatedPoints(
        tool.asset.designNode,
        paths,
        tool.pointHeightInterpolationMode,
        tool.pointHeightInterpolationSlope,
      );

      addAndCommitAction(tool, {
        commit: () => {
          changes = [];

          for (const point of interpolatedPoints) {
            const newPosition = tool.asset.designNode
              .getVertexByPointId(point.id, 'local')
              .setZ(point.height);

            changes.unshift(
              tool.asset.designNode.upsertPoint({
                id: point.id,
                vertex: newPosition,
              }),
            );
          }

          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });
    },
    true,
  );
};

function getInterpolatedPoints(
  designNode: DesignNode,
  paths: PolylinePath[],
  mode: PointHeightInterpolationMode,
  slope = 0,
): {
  id: PointId;
  height: number;
}[] {
  const linesToInterpolate: PointId[][] =
    mode === 'interpolateKeyPoints'
      ? paths.map((x) => getPointIdArray(x.nodes, x.startPoint))
      : [
          getPointIdArray(
            paths.flatMap((x) => x.nodes),
            paths[0].startPoint,
          ),
        ];

  const result: ReturnType<typeof getInterpolatedPoints> = [];

  for (const pointIds of linesToInterpolate) {
    if (pointIds.length < 2) {
      continue;
    }

    const useSlope = mode === 'slope';
    const iterationCount = useSlope ? pointIds.length : pointIds.length - 1;

    const vertices = pointIds.map((x) => designNode.getVertexByPointId(x, 'local'));
    const planDistance = calculatePolylinePlanDistances(vertices);

    for (let i = 1; i < iterationCount; i++) {
      const distanceToStart = planDistance.toStart[i];

      if (useSlope) {
        const diff = slopePercentToHeightDiff(slope, distanceToStart);
        result.push({
          id: pointIds[i],
          height: vertices[0].z + diff,
        });
      } else {
        const totalDistance = distanceToStart + planDistance.toEnd[i];
        const fraction = distanceToStart / totalDistance;

        const newHeight = interpolate(vertices[0].z, vertices.at(-1)!.z, fraction);

        result.push({
          id: pointIds[i],
          height: newHeight,
        });
      }
    }
  }

  return result;
}

/**
 * Calculate the plan distance squared to the start and to the end
 * for each point on a polyline.
 *
 * For example:
 *
 * Input: [
 *  { x: 0, y: 0, z: 0},
 *  { x: 10, y: 0, z: 0},
 *  { x: 100, y: 0, z: 0},
 * ]
 *
 * Output: {
 *  toStart: [0, 10, 100],
 *  toEnd: [100, 90, 0],
 *  toPrevious: [0, 10, 90],
 * }
 */
function calculatePolylinePlanDistances(vertices: Vector3[]) {
  const toPrevious = vertices.map((v, idx) => (idx === 0 ? 0 : flatDistance(vertices[idx - 1], v)));

  const lastIndex = vertices.length - 1;
  const toEnd: number[] = [];
  const toStart: number[] = [];

  for (const [startIndex] of vertices.entries()) {
    if (startIndex === 0) {
      toEnd.push(0);
      toStart.push(0);
      continue;
    }

    toStart.push(toStart[startIndex - 1] + toPrevious[startIndex]);
    toEnd.push(toEnd[startIndex - 1] + toPrevious[lastIndex - (startIndex - 1)]);
  }

  toEnd.reverse();

  return { toStart, toEnd, toPrevious };
}

export const interpolatePointHeightsUtils = {
  activateInterpolatePointHeightsFeature,
  initInterpolatePointHeightsAction,
  calculatePolylinePlanDistances,
};
